import { FC, JSX, useEffect, useState } from "react";
import "./Articles.scss";
import MasterPage from "../../pages/MasterPage/MasterPage";
import PageBase from "../../components/PageBase/PageBase";
import SkeletonTable from "../../components/SkeletonTable/SkeletonTable";
import AppPagination from "../../components/AppPagination/AppPagination";
import { toast } from "react-toastify";
import DropdownMenuTable from "../../components/DropdownMenuTable/DropdownMenuTable";
import { Dropdown, Modal, Toast } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBan,
  faCircleCheck,
  faCopy,
  faEdit,
  faEye,
  faFileExcel,
  faLaptopFile,
  faTrash,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import Swal from "sweetalert2";
import { usePaginate } from "../../providers/PaginateProvider";
import { useArticles } from "../../providers/Articles/ArticlesProvider";
import { useLocation } from "react-router-dom";
import moment from "moment";
import SidebarArticlesFilter from "../../components/Articles/SidebarArticlesFilter/SidebarArticlesFilter";
import ModalArticlesInsert from "../../components/Articles/ModalArticlesInsert/ModalArticlesInsert";
import { useSidebarFilter } from "../../providers/SidebarFilterProvider";
import { ArticlesModel } from "../../models/Articles/ArticleModel";
import ModalArticleHighlightsInsert from "../../components/Articles/ArticlesHighlights/ModalArticleHighlightsInsert/ModalArticleHighlightsInsert";
import ModalSortArticleInsert from "../../components/Articles/ModalSortArticleInsert/ModalSortArticleInsert";
import ModalArticleVersionInsert from "../../components/Articles/ModalArticleVersionInsert/ModalArticleVersionInsert";
import { useArticleLock } from "../../providers/Articles/ArticlesLocks/ArticlesLocksProvider";
import { useAuth } from "../../providers/AuthProvider";
import { ToastSettings } from "../../utils/ToastSettings";
import { TagModel } from "../../models/Tags/TagModel";
import ModalArticleView from "../../components/Articles/ModalArticleViewInsert/ModalArticleView";
import ModalArticlesReplaceFileBulk from "../../components/Articles/ModalArticlesReplaceFileBulk/ModalArticlesReplaceFileBulk";

interface ArticlesProps { }

const Articles: FC<ArticlesProps> = () => {
  const [showModalInsert, setShowModalInsert] = useState<boolean>(false);
  const [showModalInsertHighlights, setShowModalInsertHighlights] =
    useState<boolean>(false);
  const [showModalSortArticleInsert, setShowModalSortArticleInsert] =
    useState<boolean>(false);
  const [showModalArticleVersion, setShowModalArticleVersion] =
    useState<boolean>(false);
  const [showModalArticleView, setShowModalArticleView] =
    useState<boolean>(false);
  const [selectedArticle, setSelectedArticle] = useState<ArticlesModel | null>(
    null
  );
  const [articleToClone, setArticleToClone] = useState<ArticlesModel | null>(
    null
  );
  const [articleId, setArticleId] = useState<number>();
  const [isBulkMode, setIsBulkMode] = useState<boolean>(false);
  const [selectedBulkIds, setSelectedBulkIds] = useState<number[]>([]);
  const [isBulkDeactivating, setIsBulkDeactivating] = useState<boolean>(false);
  // Substituir anexos em lote (AI-?): modo de seleção à parte do "Inativar Materiais"
  // (selectedBulkIds), porque aqui o checkbox também precisa ficar desabilitado pra
  // artigo travado por outro usuário — regra que o deactivate_bulk não tem.
  const [isReplaceMode, setIsReplaceMode] = useState<boolean>(false);
  const [selectedReplaceIds, setSelectedReplaceIds] = useState<number[]>([]);
  const [showModalReplaceFile, setShowModalReplaceFile] = useState<boolean>(false);

  const {
    pages,
    params,
    setParams,
    isLoading,
    error,
    articles,
    setArticle,
    handleList,
    handleDelete,
    handleDeactivateBulk,
    handleReactivate,
    handleGet: getArticle,
  } = useArticles();
  const { handleGet: checkArticleLock, handleFetch: handleFetchArticleLock } = useArticleLock();
  const { handlePaginate } = usePaginate();
  const { showSidebarFilter, setShowSidebarFilter } = useSidebarFilter();
  const { userLogged } = useAuth();
  const location = useLocation();

  const handleEdit = (item: ArticlesModel) => {
    checkArticleLock(item?.id ?? 0).then((ret: boolean) => {
      if (ret) {
        handleList();
        return ToastSettings("Este artigo está sendo editado por outro usuário no momento. Você não pode editá-lo até que o bloqueio seja liberado.", 'bottom-center', 'error');
      }

      handleFetchArticleLock({ article_id: item?.id ?? 0, status: 1 }).then(() => {
        setArticle(item);
        setSelectedArticle(item);
        setArticleToClone(null);
        setShowModalInsert(true);
      });
    });
  };

  const handleCloneArticle = (item: ArticlesModel) => {
    setSelectedArticle(null);
    setArticleToClone(item);
    setShowModalInsert(true);
  };

  const handleArticleVersion = (item: ArticlesModel) => {
    setShowModalArticleVersion(true);
    setArticleId(item?.id ?? 0);
  };
  
  const handleArticleView = (item: ArticlesModel) => {
    setShowModalArticleView(true);
    setSelectedArticle(item);
  };

  const handleReactivateItem = async (itemId: number) => {
    if (itemId <= 0) return;

    const ret = await handleReactivate(itemId);

    if (ret) {
      toast.success("Seção reativada com sucesso!");
      handleList();
    }
  };

  const handleDeleteArticles = (item: ArticlesModel) => {
    Swal.fire({
      title: "<strong>Atenção?</strong>",
      html: `<span>Deseja excluir o Artigo ${item?.title}?</span>`,
      icon: "question",
      confirmButtonText: "Sim, deletar!",
      cancelButtonText: "Cancelar",
      showCancelButton: true,
      confirmButtonColor: "var(--primary)",
    }).then(async (x: any) => {
      if (x.isConfirmed) {
        const ret = await handleDelete(item.id || 0).then();
        if (ret) {
          toast.success("Artigo inativado com sucesso!");
          handleList();
        } else {
          await Swal.fire(
            "Ops!",
            `Não foi possível inativar o Artigo.<br>${error}`,
            "error"
          );
        }
      }
    });
  };

  const selectableArticleIds = articles
    ?.filter((x: ArticlesModel) => !x?.deleted_at)
    .map((x: ArticlesModel) => x.id as number) ?? [];
  const isAllSelected =
    selectableArticleIds.length > 0 &&
    selectableArticleIds.every((id: number) => selectedBulkIds.includes(id));

  const handleToggleBulkMode = () => {
    setIsBulkMode((prev) => !prev);
    setSelectedBulkIds([]);
    setIsReplaceMode(false);
    setSelectedReplaceIds([]);
  };

  const handleToggleSelectArticle = (id: number) => {
    setSelectedBulkIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleToggleSelectAll = () => {
    setSelectedBulkIds(isAllSelected ? [] : selectableArticleIds);
  };

  const handleBulkDeactivate = () => {
    if (selectedBulkIds.length === 0) return;

    Swal.fire({
      title: "<strong>Atenção?</strong>",
      html: `<span>Deseja inativar os ${selectedBulkIds.length} artigo(s) selecionado(s)?</span>`,
      icon: "question",
      confirmButtonText: "Sim, inativar!",
      cancelButtonText: "Cancelar",
      showCancelButton: true,
      confirmButtonColor: "var(--primary)",
    }).then(async (x: any) => {
      if (x.isConfirmed) {
        setIsBulkDeactivating(true);
        const ret = await handleDeactivateBulk(selectedBulkIds);
        setIsBulkDeactivating(false);

        if (ret) {
          toast.success("Artigos inativados com sucesso!");
          setSelectedBulkIds([]);
          setIsBulkMode(false);
          handleList();
        } else {
          await Swal.fire(
            "Ops!",
            `Não foi possível inativar os artigos selecionados.<br>${error}`,
            "error"
          );
        }
      }
    });
  };

  const isArticleLockedByOther = (x: ArticlesModel) =>
    !!x?.is_lock && x?.is_lock?.user_id !== userLogged?.id;

  const selectableArticleIdsReplace = articles
    ?.filter((x: ArticlesModel) => !x?.deleted_at && !isArticleLockedByOther(x))
    .map((x: ArticlesModel) => x.id as number) ?? [];
  const isAllSelectedReplace =
    selectableArticleIdsReplace.length > 0 &&
    selectableArticleIdsReplace.every((id: number) => selectedReplaceIds.includes(id));

  const handleToggleReplaceMode = () => {
    setIsReplaceMode((prev) => !prev);
    setSelectedReplaceIds([]);
    setIsBulkMode(false);
    setSelectedBulkIds([]);
  };

  const handleToggleSelectArticleReplace = (id: number) => {
    setSelectedReplaceIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleToggleSelectAllReplace = () => {
    setSelectedReplaceIds(isAllSelectedReplace ? [] : selectableArticleIdsReplace);
  };

  const handleReplaceFileDone = () => {
    setSelectedReplaceIds([]);
    setIsReplaceMode(false);
    handleList();
  };

  const renderList = (): JSX.Element => {
    return (
      <>
        {articles?.length > 0 ? (
          <>
            {articles.map((x: ArticlesModel, key: number) => (
              <tr key={x.id} className={`text-sm ${x?.is_lock ? "table-warning" : ""}`}>
                <td className="text-center col-id">
                  <div className="d-flex align-items-center justify-content-center gap-2">
                    <div className="form-check m-0 bulk-checkbox-slot">
                      {isBulkMode && !x?.deleted_at && (
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={selectedBulkIds.includes(x.id as number)}
                          onChange={() =>
                            handleToggleSelectArticle(x.id as number)
                          }
                        />
                      )}
                      {isReplaceMode && !x?.deleted_at && (
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={selectedReplaceIds.includes(x.id as number)}
                          disabled={isArticleLockedByOther(x)}
                          title={isArticleLockedByOther(x) ? "Artigo travado por outro usuário" : undefined}
                          onChange={() =>
                            handleToggleSelectArticleReplace(x.id as number)
                          }
                        />
                      )}
                    </div>
                    <span>{x.id}</span>
                  </div>
                </td>
                <td className="text-wrap col-title">
                  {x?.title}
                  {(!!x?.tags && x?.tags?.length > 0) && (
                    <div className="d-flex gap-1 mt-2">
                      {x?.tags.map((tag: TagModel) => (
                        <div key={tag.id} className="badge badge-info text-primary pt-2">{tag.content}</div>
                      ))}
                    </div>
                  )}
                </td>
                <td className="text-wrap w-30">
                  {x?.category && (
                    <p className="text-sm">
                      Categoria: <b>{x?.category?.name ?? ""}</b>
                    </p>
                  )}
                  {x?.section && (
                    <p className="text-sm">
                      Seção: <b>{x?.section?.name ?? ""}</b>
                    </p>
                  )}
                  {x?.subsection && (
                    <p className="text-sm">
                      Subseção: <b>{x?.subsection?.name ?? ""}</b>
                    </p>
                  )}
                </td>
                <td className="text-center">
                  <span
                    className={
                      x?.deleted_at ? "badge bg-danger" : "badge bg-success"
                    }
                  >
                    {x?.deleted_at ? "Inativo" : "Ativo"}
                  </span>
                </td>
                <td className="text-center">
                  <p className="text-sm">
                    <b>{x?.is_internal ? "Sim" : "Não"}</b>
                  </p>
                </td>
                <td>
                  <p className="text-sm">
                    Usuário cadastro:
                    <br />
                    <b>
                      {x?.user_created?.partner_code ?? ""} -{" "}
                      {x?.user_created?.name ?? ""}
                    </b>
                  </p>
                  <p className="text-sm">
                    Usuário atualização:
                    <br />
                    <b>
                      {x?.user_updated?.partner_code ?? ""} -{" "}
                      {x?.user_updated?.name ?? ""}
                    </b>
                  </p>
                  <p className="text-sm">
                    Usuário editando:
                    <br />
                    <b>
                      {x?.is_lock ? (
                        <>
                          {x?.is_lock?.partner_code ?? ""} -{" "}
                          {x?.is_lock?.name ?? ""} -{" "}
                          {moment(x?.is_lock?.created_at).format("DD/MM/YYYY HH:mm:ss") ?? ""}
                        </>
                      ) : ("-")}
                    </b>
                  </p>
                </td>
                <td>
                  <p className="text-sm">
                    Data Cadastro:
                    <br />
                    <b>{moment(x?.created_at).format("DD/MM/YYYY HH:mm:ss")}</b>
                  </p>
                  <p className="text-sm">
                    Data Atualizado:
                    <br />
                    <b>{moment(x?.updated_at).format("DD/MM/YYYY HH:mm:ss")}</b>
                  </p>
                </td>
                <td className="text-center">
                  <DropdownMenuTable key={key}>
                    <Dropdown.Item
                      className="border-radius-md py-2"
                      onClick={() => handleArticleVersion(x)}
                    >
                      <FontAwesomeIcon icon={faLaptopFile} className="me-2" />
                      Ver histórico
                    </Dropdown.Item>
                    <Dropdown.Item
                      className="border-radius-md py-2"
                      onClick={() => handleArticleView(x)}
                    >
                      <FontAwesomeIcon icon={faEye} className="me-2" />
                      Ver conteúdo
                    </Dropdown.Item>
                    <Dropdown.Item
                      className="border-radius-md py-2"
                      onClick={() => handleCloneArticle(x)}
                    >
                      <FontAwesomeIcon icon={faCopy} className="me-2" />
                      Clonar artigo
                    </Dropdown.Item>
                    {(!x?.deleted_at && (!x?.is_lock || x?.is_lock?.user_id === userLogged?.id)) && (
                      <>
                        <Dropdown.Item
                          className="border-radius-md py-2"
                          onClick={() => handleEdit(x)}
                        >
                          <FontAwesomeIcon icon={faEdit} className="me-2" />{" "}
                          Editar
                        </Dropdown.Item>
                        <Dropdown.Item
                          className="border-radius-md py-2"
                          onClick={() => handleDeleteArticles(x)}
                        >
                          <FontAwesomeIcon icon={faTrash} className="me-2" />{" "}
                          Remover
                        </Dropdown.Item>
                      </>
                    )}
                    {x?.deleted_at && (
                      <Dropdown.Item
                        className="border-radius-md py-2"
                        onClick={() => handleReactivateItem(x?.id ?? 0)}
                      >
                        <FontAwesomeIcon
                          icon={faCircleCheck}
                          className="me-2"
                        />
                        Reativar
                      </Dropdown.Item>
                    )}
                  </DropdownMenuTable>
                </td>
              </tr>
            ))}
          </>
        ) : (
          <tr>
            <td colSpan={8} className="text-center text-sm">
              Não há registros
            </td>
          </tr>
        )}
      </>
    );
  };

  const renderContent = (): JSX.Element => {
    return (
      <>
        {!isLoading ? (
          <div>
            <div className="table-responsive">
              <table className="table table-flush m-0 w-100">
                <thead className="thead-light">
                  <tr>
                    <th
                      className="text-uppercase opacity-7 text-center col-id"
                      scope="col"
                    >
                      <div className="d-flex align-items-center justify-content-center gap-2">
                        <div className="form-check m-0 bulk-checkbox-slot">
                          {isBulkMode && (
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={isAllSelected}
                              onChange={handleToggleSelectAll}
                              disabled={selectableArticleIds.length === 0}
                            />
                          )}
                          {isReplaceMode && (
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={isAllSelectedReplace}
                              onChange={handleToggleSelectAllReplace}
                              disabled={selectableArticleIdsReplace.length === 0}
                            />
                          )}
                        </div>
                        <span>#</span>
                      </div>
                    </th>
                    <th
                      className="text-uppercase opacity-7 col-medium"
                      scope="col"
                    >
                      Título
                    </th>
                    <th className="text-uppercase opacity-7 w-30" scope="col">
                      Categoria/Seção/Subseção
                    </th>
                    <th
                      className="text-uppercase opacity-7 text-center"
                      scope="col"
                    >
                      Status
                    </th>
                    <th
                      className="text-uppercase opacity-7 text-center"
                      scope="col"
                    >
                      Interno
                    </th>
                    <th className="text-uppercase opacity-7" scope="col">
                      Usuários
                    </th>
                    <th className="text-uppercase opacity-7" scope="col">
                      Datas
                    </th>
                    <th
                      className="text-uppercase opacity-7 text-center"
                      scope="col"
                    >
                      Ação
                    </th>
                  </tr>
                </thead>
                <tbody>{renderList()}</tbody>
              </table>
            </div>

            {articles?.length > 0 && (
              <div>
                <nav aria-label="...">
                  <AppPagination
                    pages={pages}
                    handlePaginate={handlePaginate}
                    params={params}
                    setParams={setParams}
                    style={{ backgroundColor: "#fff" }}
                  />
                </nav>
              </div>
            )}
          </div>
        ) : (
          <SkeletonTable />
        )}
      </>
    );
  };

  useEffect(() => {
    setSelectedArticle(null);
    setParams({ ...params, page: 1, filter: { only_active: null } });
  }, []);

  useEffect(() => {
    setSelectedBulkIds([]);
    setSelectedReplaceIds([]);
  }, [params]);

  useEffect(() => {
    const state = location.state as { editArticleId?: string };
    if (state?.editArticleId) {
      const articleId = parseInt(state.editArticleId);

      const fetchArticleForEdit = async () => {
        const article = await getArticle(articleId);
        if (article) {
          handleEdit(article);
        }
      };

      fetchArticleForEdit();
    }
  }, []);

  return (
    <MasterPage title="Artigos" icon="fas fa-file" footerFixed>
      <div className="Articles" data-testid="Articles">
        <PageBase
          title="Artigos"
          subtitle="Gerenciamento de Artigos."
          handleInsert={() => {
            setSelectedArticle(null);
            setArticleToClone(null);
            setShowModalInsert(true);
          }}
          handleFilter={() =>
            !!showSidebarFilter
              ? setShowSidebarFilter(false)
              : setShowSidebarFilter(true)
          }
          error={error}
          children={renderContent()}
          renderBtnAux={
            <div>
              <button
                type="button"
                className="btn btn-outline-primary text-primary btn-sm mb-0"
                onClick={() => setShowModalInsertHighlights(true)}
              >
                +&nbsp; Destaques
              </button>
            </div>
          }
          renderBtnAux2={
            <div>
              <button
                type="button"
                className="btn btn-outline-primary text-primary btn-sm mb-0"
                onClick={() => setShowModalSortArticleInsert(true)}
              >
                +&nbsp; Ordernar Artigos
              </button>
            </div>
          }
          renderBtnAux3={
            <div className="d-flex gap-2">
              {!isBulkMode ? (
                <button
                  type="button"
                  className="btn btn-outline-primary text-primary btn-sm mb-0 btn-bulk-toggle"
                  onClick={handleToggleBulkMode}
                >
                  <FontAwesomeIcon icon={faBan} className="me-2" />
                  Inativar Materiais
                </button>
              ) : selectedBulkIds.length > 0 ? (
                <button
                  type="button"
                  className="btn btn-outline-primary text-primary btn-sm mb-0 btn-bulk-toggle"
                  onClick={handleBulkDeactivate}
                  disabled={isBulkDeactivating}
                >
                  <FontAwesomeIcon icon={faBan} className="me-2" />
                  Inativar selecionados ({selectedBulkIds.length})
                </button>
              ) : (
                <button
                  type="button"
                  className="btn btn-outline-primary text-primary btn-sm mb-0 btn-bulk-toggle"
                  onClick={handleToggleBulkMode}
                >
                  <FontAwesomeIcon icon={faXmark} className="me-2" />
                  Cancelar inativação
                </button>
              )}
              {!isReplaceMode ? (
                <button
                  type="button"
                  className="btn btn-outline-primary text-primary btn-sm mb-0 btn-bulk-toggle"
                  onClick={handleToggleReplaceMode}
                >
                  <FontAwesomeIcon icon={faFileExcel} className="me-2" />
                  Substituir Anexos
                </button>
              ) : selectedReplaceIds.length > 0 ? (
                <button
                  type="button"
                  className="btn btn-outline-primary text-primary btn-sm mb-0 btn-bulk-toggle"
                  onClick={() => setShowModalReplaceFile(true)}
                >
                  <FontAwesomeIcon icon={faFileExcel} className="me-2" />
                  Substituir selecionados ({selectedReplaceIds.length})
                </button>
              ) : (
                <button
                  type="button"
                  className="btn btn-outline-primary text-primary btn-sm mb-0 btn-bulk-toggle"
                  onClick={handleToggleReplaceMode}
                >
                  <FontAwesomeIcon icon={faXmark} className="me-2" />
                  Cancelar substituição
                </button>
              )}
            </div>
          }
          hasFilter
          scrollableButtons
        />

        <ModalArticlesInsert
          show={showModalInsert}
          onClose={() => {
            setShowModalInsert(false);
            setArticleToClone(null);
          }}
          article_edit={selectedArticle}
          article_clone={articleToClone}
        />
        
        <ModalArticleView
          show={showModalArticleView}
          onClose={() => setShowModalArticleView(false)}
          article={selectedArticle}
        />

        <ModalArticleHighlightsInsert
          show={showModalInsertHighlights}
          onClose={() => setShowModalInsertHighlights(false)}
        />

        <ModalSortArticleInsert
          show={showModalSortArticleInsert}
          onClose={() => setShowModalSortArticleInsert(false)}
        />

        <ModalArticleVersionInsert
          show={showModalArticleVersion}
          onClose={() => setShowModalArticleVersion(false)}
          articleId={articleId ?? 0}
        />

        <ModalArticlesReplaceFileBulk
          show={showModalReplaceFile}
          ids={selectedReplaceIds}
          onClose={() => setShowModalReplaceFile(false)}
          onDone={handleReplaceFileDone}
        />
      </div>

      <SidebarArticlesFilter />
    </MasterPage>
  );
};

export default Articles;
